import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Table,
  Pagination,
  Text,
  Box,
  UnstyledButton,
  Center,
  Skeleton,
  Group,
  Stack,
} from '@mantine/core';
import {
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconDatabaseOff,
} from '@tabler/icons-react';

export interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: number | string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
  };
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  actions?: (row: T) => React.ReactNode;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  getRowKey?: (row: T) => string;
}

export function DataTable<T extends Record<string, unknown>>({
  data,
  columns,
  loading = false,
  pagination,
  onRowClick,
  emptyMessage = 'No records found',
  actions,
  onSort,
  getRowKey,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.pageSize)
    : 0;

  const handleSort = (key: string) => {
    const newDirection =
      sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDirection(newDirection);
    onSort?.(key, newDirection);
  };

  const SortIcon = ({ columnKey }: { columnKey: string }) => {
    if (sortKey !== columnKey)
      return <IconSelector size={14} stroke={1.5} />;
    return sortDirection === 'asc' ? (
      <IconChevronUp size={14} stroke={1.5} />
    ) : (
      <IconChevronDown size={14} stroke={1.5} />
    );
  };

  const allColumns = actions
    ? [...columns, { key: '__actions', label: 'Actions', width: 120 }]
    : columns;

  if (loading) {
    return (
      <Box>
        <Table.ScrollContainer minWidth={800}>
          <Table stickyHeader>
            <Table.Thead>
              <Table.Tr>
                {allColumns.map((col) => (
                  <Table.Th key={col.key} w={col.width}>
                    {col.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {Array.from({ length: 5 }).map((_: any, i) => (
                <Table.Tr key={i}>
                  {allColumns.map((col) => (
                    <Table.Td key={col.key}>
                      <Skeleton height={20} />
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box>
        <Table.ScrollContainer minWidth={800}>
          <Table stickyHeader>
            <Table.Thead>
              <Table.Tr>
                {allColumns.map((col) => (
                  <Table.Th key={col.key} w={col.width}>
                    {col.label}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td colSpan={allColumns.length}>
                  <Center py="xl">
                    <Stack ta="center" gap="xs">
                      <IconDatabaseOff
                        size={48}
                        stroke={1.5}
                        color="var(--mantine-color-dimmed)"
                      />
                      <Text c="dimmed" size="sm">
                        {emptyMessage}
                      </Text>
                    </Stack>
                  </Center>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Box>
    );
  }

  return (
    <Box>
      <Table.ScrollContainer minWidth={800}>
        <Table stickyHeader>
          <Table.Thead>
            <Table.Tr>
              {columns.map((col) => (
                <Table.Th key={col.key} w={col.width}>
                  {col.sortable ? (
                    <UnstyledButton onClick={() => handleSort(col.key)}>
                      <Group gap={4} wrap="nowrap">
                        <Text fw={600} fz="sm">
                          {col.label}
                        </Text>
                        <Center>
                          <SortIcon columnKey={col.key} />
                        </Center>
                      </Group>
                    </UnstyledButton>
                  ) : (
                    <Text fw={600} fz="sm">
                      {col.label}
                    </Text>
                  )}
                </Table.Th>
              ))}
              {actions && (
                <Table.Th w={120}>
                  <Text fw={600} fz="sm">
                    Actions
                  </Text>
                </Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.map((row, index) => (
              <Table.Tr
                key={getRowKey ? getRowKey(row) : index}
                onClick={() => onRowClick?.(row)}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((col) => (
                  <Table.Td key={col.key}>
                    {col.render
                      ? col.render(row)
                      : String(row[col.key] ?? '')}
                  </Table.Td>
                ))}
                {actions && (
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    {actions(row)}
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {pagination && totalPages > 0 && (
        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">
            Showing{' '}
            {(pagination.page - 1) * pagination.pageSize + 1} to{' '}
            {Math.min(pagination.page * pagination.pageSize, pagination.total)}{' '}
            of {pagination.total} records
          </Text>
          {totalPages > 1 && (
            <Pagination
              total={totalPages}
              value={pagination.page}
              onChange={pagination.onPageChange}
              size="sm"
            />
          )}
        </Group>
      )}
    </Box>
  );
}
